/**
 * ChatWidget Component
 * Modern chatbot UI with structured JSON response support
 */

import React, { useState, useRef, useEffect } from 'react';
import { useChat } from '../../hooks/useChat';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ChatbotAvatar from './ChatbotAvatar';
import EmptyState from './EmptyState';
import SuggestionChips from './SuggestionChips';
import ErrorMessage from './ErrorMessage';
import SkeletonLoader from './SkeletonLoader';
import './ChatWidget.css';

// Message Bubble Component
interface MessageBubbleProps {
  message: {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    intent?: string;
    createdAt: string;
  };
  onCopy?: (text: string, id: string) => void;
  copiedId?: string | null;
  onReaction?: (messageId: string, reaction: 'up' | 'down') => void;
  onSuggestionClick?: (suggestion: string) => void;
}

const formatRichMarkdown = (text: string): string => {
  const escapeHtml = (value: string) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const inline = (value: string) => escapeHtml(value)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>');

  const isSeparator = (line: string) =>
    /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
  const splitCells = (line: string) =>
    line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());
  const alignFor = (cell: string) => {
    const value = cell.trim();
    if (value.startsWith(':') && value.endsWith(':')) return 'center';
    if (value.endsWith(':')) return 'right';
    return 'left';
  };

  const lines = String(text || '').split('\n');
  const html: string[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const next = lines[i + 1];

    if (line.includes('|') && next && isSeparator(next)) {
      const headers = splitCells(line);
      const aligns = splitCells(next).map(alignFor);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitCells(lines[i]));
        i += 1;
      }
      i -= 1;

      html.push('<div class="chat-md-table-wrap"><table class="chat-md-table"><thead><tr>');
      headers.forEach((header, index) => {
        html.push(`<th style="text-align:${aligns[index] || 'left'}">${inline(header)}</th>`);
      });
      html.push('</tr></thead><tbody>');
      rows.forEach(row => {
        html.push('<tr>');
        headers.forEach((_, index) => {
          html.push(`<td style="text-align:${aligns[index] || 'left'}">${inline(row[index] || '')}</td>`);
        });
        html.push('</tr>');
      });
      html.push('</tbody></table></div>');
      continue;
    }

    if (!line.trim()) {
      html.push('<div class="chat-md-space"></div>');
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const level = Math.min(heading[1].length, 4);
      html.push(`<h${level} class="chat-md-heading chat-md-heading-${level}">${inline(heading[2])}</h${level}>`);
      continue;
    }

    const numbered = line.match(/^\s*(\d+)\.\s+(.+)$/);
    if (numbered) {
      html.push(`<div class="chat-md-list-row"><span>${numbered[1]}.</span><p>${inline(numbered[2])}</p></div>`);
      continue;
    }

    const bullet = line.match(/^\s*[-•]\s+(.+)$/);
    if (bullet) {
      html.push(`<div class="chat-md-list-row"><span>•</span><p>${inline(bullet[1])}</p></div>`);
      continue;
    }

    html.push(`<p class="chat-md-paragraph">${inline(line)}</p>`);
  }

  return html.join('');
};

const getTokenUsage = (usage: any) => {
  if (!usage) return null;
  const inputTokens = usage.inputTokens ?? usage.input;
  const outputTokens = usage.outputTokens ?? usage.output;
  const totalTokens = usage.totalTokens ?? (
    inputTokens != null || outputTokens != null
      ? Number(inputTokens || 0) + Number(outputTokens || 0)
      : undefined
  );

  return {
    inputTokens,
    outputTokens,
    totalTokens,
    estimated: Boolean(usage.estimated),
    semanticCacheHit: Boolean(usage.semanticCacheHit),
  };
};

const TokenUsageLine: React.FC<{ usage: any }> = ({ usage }) => {
  const tokens = getTokenUsage(usage);
  if (!tokens || (tokens.inputTokens == null && tokens.outputTokens == null)) return null;

  return (
    <div className="chat-token-usage">
      Input tokens: {tokens.inputTokens ?? 0} | Output tokens: {tokens.outputTokens ?? 0} | Total tokens: {tokens.totalTokens ?? 0}
      {tokens.semanticCacheHit ? ' | cached' : ''}
      {tokens.estimated && !tokens.semanticCacheHit ? ' | estimated' : ''}
    </div>
  );
};

const removeWritingOptions = (response: any) => {
  if (!response || typeof response !== 'object') return response;
  const cleaned = { ...response };
  delete cleaned.primary_actions;
  delete cleaned.secondary_actions;
  delete cleaned.quickActions;
  delete cleaned.suggestions;
  return cleaned;
};

const MessageBubble: React.FC<MessageBubbleProps> = ({ 
  message, 
  onCopy, 
  copiedId, 
  onReaction,
  onSuggestionClick 
}) => {
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ACTION HANDLER - Handle all UI actions
  const handleAction = (action: string, data?: any) => {
    console.log('[ChatWidget] Action triggered:', action, data);
    
    switch (action) {
      case 'submit_expense':
        window.location.href = '/dashboard';
        break;
        
      case 'view_all_expenses':
        window.location.href = '/dashboard';
        break;
        
      case 'filter_expenses':
        window.location.href = '/dashboard';
        break;
        
      case 'export_expenses':
        window.location.href = '/dashboard';
        break;
        
      case 'view_team_expenses':
        window.location.href = '/manager';
        break;
        
      case 'view_expense_detail':
        window.location.href = '/dashboard';
        break;
        
      case 'change_filter':
        window.location.href = '/dashboard';
        break;
        
      case 'view_performance':
        window.location.href = '/performance/objectives';
        break;
        
      case 'view_nominations':
        window.location.href = '/nominations/feed';
        break;
        
      case 'view_timesheets':
        window.location.href = '/timesheet';
        break;
        
      default:
        console.warn('[ChatWidget] Unknown action:', action);
    }
  };

  // Render user message (simple text)
  if (message.role === 'user') {
    return (
      <div className="message-bubble user">
        <div className="message-content">
          <div className="message-text">{message.content}</div>
        </div>
        <div className="message-footer">
          <div className="message-timestamp">{formatTime(message.createdAt)}</div>
        </div>
      </div>
    );
  }

  // Parse assistant message (JSON response)
  let parsed: any;
  try {
    parsed = typeof message.content === 'string' 
      ? JSON.parse(message.content) 
      : message.content;
    parsed = removeWritingOptions(parsed);
    
    // Debug logging
    console.log('[ChatWidget] Parsed message:', {
      type: parsed.type,
      typeOf: typeof parsed.type,
      hasItems: !!parsed.items,
      itemCount: parsed.items?.length,
      hasSummary: !!parsed.summary
    });
    console.log('[ChatWidget] Full parsed object:', parsed);
  } catch (error) {
    // Fallback to plain text if JSON parsing fails
    console.error('[ChatWidget] Failed to parse message content:', error);
    console.error('[ChatWidget] Raw content:', message.content);
    parsed = { type: 'text', message: message.content };
  }

  // Render based on response type
  const renderContent = () => {
    switch (parsed.type) {
      case 'expense_list':
        return (
          <div className="response-container clean-response">
            {parsed.title && (
              <div className="response-title">
                {parsed.title}
              </div>
            )}
            
            {/* Show summary as clean text */}
            {parsed.summary && (
              <div className="response-summary-text">
                Found {parsed.summary.totalCount || parsed.summary.count || 0} expenses • Total: {parsed.summary.currency || '₹'}{parsed.summary.totalAmount || parsed.summary.total_amount || 0}
              </div>
            )}
            
            {/* Show expense items in clean format */}
            {parsed.items && parsed.items.length > 0 && (
              <div className="expense-items-clean">
                {parsed.items.map((expense: any) => (
                  <div key={expense.id} className="expense-item-clean">
                    <div className="expense-item-header">
                      <div className="expense-item-title">{expense.title}</div>
                      <div className={`expense-item-status status-${expense.status.toLowerCase()}`}>
                        {expense.status}
                      </div>
                    </div>
                    <div className="expense-item-details">
                      <div className="expense-item-row">
                        <span className="expense-item-label">Amount:</span>
                        <span className="expense-item-value expense-amount">{expense.currency || '₹'}{expense.amount}</span>
                      </div>
                      <div className="expense-item-row">
                        <span className="expense-item-label">Date:</span>
                        <span className="expense-item-value">{new Date(expense.date).toLocaleDateString()}</span>
                      </div>
                      <div className="expense-item-row">
                        <span className="expense-item-label">Category:</span>
                        <span className="expense-item-value">{expense.category}</span>
                      </div>
                      {expense.description && (
                        <div className="expense-item-row">
                          <span className="expense-item-label">Description:</span>
                          <span className="expense-item-value">{expense.description}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* PRIMARY ACTION BUTTONS */}
            {parsed.primary_actions && parsed.primary_actions.length > 0 && (
              <div className="primary-action-bar">
                {parsed.primary_actions.map((action: any, index: number) => (
                  <button
                    key={index}
                    className="primary-action-btn"
                    onClick={() => handleAction(action.action)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
            
            {/* SECONDARY ACTIONS */}
            {parsed.secondary_actions && parsed.secondary_actions.length > 0 && (
              <div className="secondary-action-bar">
                {parsed.secondary_actions.map((action: any, index: number) => (
                  <button
                    key={index}
                    className="secondary-action-btn"
                    onClick={() => handleAction(action.action)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
            
            {/* AI GUIDANCE */}
            {parsed.ai_guidance && (
              <div className="ai-guidance">
                💡 {parsed.ai_guidance}
              </div>
            )}
            
            {parsed.suggestions && parsed.suggestions.length > 0 && (
              <SuggestionChips 
                suggestions={parsed.suggestions} 
                onSuggestionClick={onSuggestionClick!} 
              />
            )}
          </div>
        );

      case 'empty':
        return (
          <div className="response-container">
            <EmptyState
              title={parsed.title}
              message={parsed.message}
              suggestions={parsed.suggestions}
              onSuggestionClick={onSuggestionClick}
            />
            
            {/* PRIMARY ACTION BAR FOR EMPTY STATE */}
            {parsed.primary_actions && parsed.primary_actions.length > 0 && (
              <div className="primary-action-bar">
                {parsed.primary_actions.map((action: any, index: number) => (
                  <button
                    key={index}
                    className="primary-action-btn"
                    onClick={() => handleAction(action.action)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
            
            {/* AI GUIDANCE */}
            {parsed.ai_guidance && (
              <div className="ai-guidance">
                {parsed.ai_guidance}
              </div>
            )}
          </div>
        );

      case 'error':
        return (
          <div className="response-container">
            
            <ErrorMessage
              title={parsed.title}
              message={parsed.message}
              code={parsed.code}
              details={parsed.details}
              suggestions={parsed.suggestions}
              onSuggestionClick={onSuggestionClick}
            />
          </div>
        );

      case 'clarification':
        return (
          <div className="response-container">
            <div className="response-title">
              {parsed.title}
            </div>
            <div className="response-message">{parsed.message}</div>
            {parsed.suggestions && parsed.suggestions.length > 0 && (
              <SuggestionChips 
                suggestions={parsed.suggestions} 
                onSuggestionClick={onSuggestionClick!} 
              />
            )}
          </div>
        );

      case 'greeting':
        return (
          <div className="response-container">
            <div className="response-title">
              {parsed.title}
            </div>
            {parsed.message && <div className="response-message">{parsed.message}</div>}
            {parsed.quickActions && parsed.quickActions.length > 0 && (
              <div className="quick-actions">
                {parsed.quickActions.map((action: any, index: number) => (
                  <button
                    key={index}
                    className="quick-action-btn"
                    onClick={() => onSuggestionClick?.(action.query)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );

      case 'llm_response':
        return (
          <div className="response-container llm-response">
            <div className="response-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ai-icon">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
              {parsed.title}
            </div>
            <div className="response-message llm-message">
              {parsed.message}
            </div>
            {parsed.suggestions && parsed.suggestions.length > 0 && (
              <SuggestionChips 
                suggestions={parsed.suggestions} 
                onSuggestionClick={onSuggestionClick!} 
              />
            )}
          </div>
        );

      case 'help':
        return (
          <div className="response-container">
            <div className="response-title">
              {parsed.title}
            </div>
            {parsed.commands && parsed.commands.length > 0 && (
              <div className="help-commands">
                <div className="help-section-title">Available Commands:</div>
                <ul className="help-list">
                  {parsed.commands.map((cmd: string, index: number) => (
                    <li key={index}>{cmd}</li>
                  ))}
                </ul>
              </div>
            )}
            {parsed.examples && parsed.examples.length > 0 && (
              <div className="help-examples">
                <div className="help-section-title">Examples:</div>
                <ul className="help-list">
                  {parsed.examples.map((ex: string, index: number) => (
                    <li key={index}>{ex}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'team_list':
        return (
          <div className="response-container">
            {parsed.title && (
              <div className="response-title">
                {parsed.title}
              </div>
            )}
            {parsed.subtitle && <div className="response-subtitle">{parsed.subtitle}</div>}
            {parsed.summary && (
              <div className="team-summary">
                <span className="summary-item">Total: {parsed.summary.totalCount}</span>
                {parsed.summary.completeProfiles > 0 && (
                  <span className="summary-item summary-green">Complete: {parsed.summary.completeProfiles}</span>
                )}
                {parsed.summary.incompleteProfiles > 0 && (
                  <span className="summary-item summary-yellow">Incomplete: {parsed.summary.incompleteProfiles}</span>
                )}
              </div>
            )}
            <div className="team-list">
              {parsed.items?.map((member: any) => (
                <div key={member.id} className={`team-card ${!member.hasCompleteProfile ? 'incomplete-profile' : ''}`}>
                  <div className="team-card-header">
                    <div className="team-member-name">
                      {member.name}
                      {!member.hasCompleteProfile && (
                        <span className="profile-indicator" title="Profile incomplete - extracted from expense records">⚠️</span>
                      )}
                    </div>
                    <div className="team-member-role">{member.role}</div>
                  </div>
                  <div className="team-card-body">
                    <div className="team-card-row">
                      <span className="team-card-label">Email</span>
                      <span className="team-card-value">{member.email}</span>
                    </div>
                    {member.department && (
                      <div className="team-card-row">
                        <span className="team-card-label">Department</span>
                        <span className="team-card-value">{member.department}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {parsed.suggestions && parsed.suggestions.length > 0 && (
              <SuggestionChips 
                suggestions={parsed.suggestions} 
                onSuggestionClick={onSuggestionClick!} 
              />
            )}
          </div>
        );

      case 'employee_list':
        return (
          <div className="response-container">
            {parsed.title && (
              <div className="response-title">
                {parsed.title}
              </div>
            )}
            {parsed.subtitle && <div className="response-subtitle">{parsed.subtitle}</div>}
            {parsed.summary && (
              <div className="employee-summary">
                <span className="summary-item">Total: {parsed.summary.totalCount}</span>
                {parsed.summary.completeProfiles > 0 && (
                  <span className="summary-item summary-green">Complete: {parsed.summary.completeProfiles}</span>
                )}
                {parsed.summary.incompleteProfiles > 0 && (
                  <span className="summary-item summary-yellow">Incomplete: {parsed.summary.incompleteProfiles}</span>
                )}
              </div>
            )}
            <div className="employee-list">
              {parsed.items?.map((employee: any) => (
                <div key={employee.id} className={`employee-card ${!employee.hasCompleteProfile ? 'incomplete-profile' : ''}`}>
                  <div className="employee-card-header">
                    <div className="employee-name">
                      {employee.name}
                      {!employee.hasCompleteProfile && (
                        <span className="profile-indicator" title="Profile incomplete - extracted from expense records">⚠️</span>
                      )}
                    </div>
                    <div className="employee-role">{employee.role}</div>
                  </div>
                  <div className="employee-card-body">
                    <div className="employee-card-row">
                      <span className="employee-card-label">Email</span>
                      <span className="employee-card-value">{employee.email}</span>
                    </div>
                    <div className="employee-card-row">
                      <span className="employee-card-label">Department</span>
                      <span className="employee-card-value">{employee.department}</span>
                    </div>
                    {employee.manager && (
                      <div className="employee-card-row">
                        <span className="employee-card-label">Manager</span>
                        <span className="employee-card-value">{employee.manager}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {parsed.suggestions && parsed.suggestions.length > 0 && (
              <SuggestionChips 
                suggestions={parsed.suggestions} 
                onSuggestionClick={onSuggestionClick!} 
              />
            )}
          </div>
        );

      case 'objective_list':
        return (
          <div className="response-container">
            {parsed.title && (
              <div className="response-title">
                {parsed.title}
              </div>
            )}
            {parsed.summary && (
              <div className="objective-summary">
                <span className="summary-item">Total: {parsed.summary.totalCount}</span>
                {parsed.summary.completed > 0 && (
                  <span className="summary-item summary-green">Completed: {parsed.summary.completed}</span>
                )}
                {parsed.summary.inProgress > 0 && (
                  <span className="summary-item summary-blue">In Progress: {parsed.summary.inProgress}</span>
                )}
                {parsed.summary.notStarted > 0 && (
                  <span className="summary-item summary-gray">Not Started: {parsed.summary.notStarted}</span>
                )}
              </div>
            )}
            <div className="objective-list">
              {parsed.items?.map((obj: any) => (
                <div key={obj.id} className="objective-card">
                  <div className="objective-card-header">
                    <div className="objective-title">{obj.title}</div>
                    <div className={`objective-status status-${obj.status.toLowerCase()}`}>
                      {obj.status}
                    </div>
                  </div>
                  {obj.description && (
                    <div className="objective-description">{obj.description}</div>
                  )}
                  <div className="objective-footer">
                    {obj.progress !== undefined && (
                      <div className="objective-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${obj.progress}%` }}
                          />
                        </div>
                        <span className="progress-text">{obj.progress}%</span>
                      </div>
                    )}
                    {obj.dueDate && (
                      <div className="objective-due">Due: {new Date(obj.dueDate).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'nomination_list':
        return (
          <div className="response-container">
            {parsed.title && (
              <div className="response-title">
                {parsed.title}
              </div>
            )}
            {parsed.summary && (
              <div className="nomination-summary">
                <span className="summary-item">Total: {parsed.summary.totalCount}</span>
                {parsed.summary.statusBreakdown && (
                  <>
                    {parsed.summary.statusBreakdown.submitted > 0 && (
                      <span className="summary-item summary-blue">Submitted: {parsed.summary.statusBreakdown.submitted}</span>
                    )}
                    {parsed.summary.statusBreakdown.approved > 0 && (
                      <span className="summary-item summary-green">Approved: {parsed.summary.statusBreakdown.approved}</span>
                    )}
                    {parsed.summary.statusBreakdown.rejected > 0 && (
                      <span className="summary-item summary-red">Rejected: {parsed.summary.statusBreakdown.rejected}</span>
                    )}
                    {parsed.summary.statusBreakdown.pending > 0 && (
                      <span className="summary-item summary-yellow">Pending: {parsed.summary.statusBreakdown.pending}</span>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="nomination-list">
              {parsed.items?.map((nom: any) => (
                <div key={nom.id} className="nomination-card">
                  <div className="nomination-card-header">
                    <div className="nomination-nominee">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      {nom.nominee}
                    </div>
                    <div className={`nomination-status status-${nom.status.toLowerCase()}`}>
                      {nom.status}
                    </div>
                  </div>
                  {nom.category && (
                    <div className="nomination-category">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                      {nom.category}
                    </div>
                  )}
                  {nom.reason && (
                    <div className="nomination-reason">{nom.reason.length > 150 ? nom.reason.substring(0, 150) + '...' : nom.reason}</div>
                  )}
                  <div className="nomination-footer">
                    {nom.nominator && (
                      <div className="nomination-nominator">By: {nom.nominator}</div>
                    )}
                    {nom.date && (
                      <div className="nomination-date">{new Date(nom.date).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {parsed.suggestions && parsed.suggestions.length > 0 && (
              <SuggestionChips 
                suggestions={parsed.suggestions} 
                onSuggestionClick={onSuggestionClick!} 
              />
            )}
          </div>
        );

      case 'multi_result':
        return (
          <div className="response-container">
            {parsed.title && (
              <div className="response-title">
                {parsed.title}
              </div>
            )}
            {parsed.sections?.map((section: any, index: number) => (
              <div key={index} className="multi-result-section">
                <MessageBubble
                  message={{
                    ...message,
                    content: JSON.stringify(section)
                  }}
                  onSuggestionClick={onSuggestionClick}
                />
              </div>
            ))}
          </div>
        );

      case 'hierarchy_info':
        return (
          <div className="response-container hierarchy-response">
            {parsed.title && (
              <div className="response-title">
                {parsed.title}
              </div>
            )}
            {parsed.message && <div className="response-message">{parsed.message}</div>}
            
            {/* Organizational Structure */}
            {parsed.structure && (
              <div className="hierarchy-section">
                <div className="hierarchy-section-title">{parsed.structure.title}</div>
                <div className="hierarchy-levels">
                  {parsed.structure.levels?.map((level: any, index: number) => (
                    <div key={index} className="hierarchy-level">
                      <div className="level-header">
                        <span className="level-role">{level.role}</span>
                        <span className="level-position">Level {level.position}</span>
                      </div>
                      <div className="level-description">{level.description}</div>
                      {level.keyFunctions && level.keyFunctions.length > 0 && (
                        <div className="level-functions">
                          <div className="functions-title">Key Functions:</div>
                          <ul className="functions-list">
                            {level.keyFunctions.map((func: string, idx: number) => (
                              <li key={idx}>{func}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Workflow Process */}
            {parsed.workflow && (
              <div className="hierarchy-section">
                <div className="hierarchy-section-title">{parsed.workflow.title}</div>
                {parsed.workflow.process && (
                  <div className="workflow-process">
                    {parsed.workflow.process.map((step: string, index: number) => (
                      <div key={index} className="workflow-step">{step}</div>
                    ))}
                  </div>
                )}
                {parsed.workflow.flowChart && (
                  <div className="workflow-chart">
                    <div className="chart-title">Flow:</div>
                    <div className="chart-content">{parsed.workflow.flowChart}</div>
                  </div>
                )}
              </div>
            )}

            {/* User Profile */}
            {parsed.yourProfile && (
              <div className="hierarchy-section user-profile">
                <div className="hierarchy-section-title">{parsed.yourProfile.title}</div>
                <div className="profile-content">
                  <div className="profile-role">
                    <strong>Role:</strong> {parsed.yourProfile.role}
                  </div>
                  {parsed.yourProfile.status && (
                    <div className="profile-status">{parsed.yourProfile.status}</div>
                  )}
                  {parsed.yourProfile.manager && (
                    <div className="profile-manager">
                      <strong>Manager:</strong> {parsed.yourProfile.manager}
                    </div>
                  )}
                  {parsed.yourProfile.permissions && parsed.yourProfile.permissions.length > 0 && (
                    <div className="profile-permissions">
                      <div className="permissions-title">Your Permissions:</div>
                      <ul className="permissions-list">
                        {parsed.yourProfile.permissions.map((permission: string, idx: number) => (
                          <li key={idx}>{permission}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {parsed.yourProfile.nextSteps && parsed.yourProfile.nextSteps.length > 0 && (
                    <div className="profile-next-steps">
                      <div className="next-steps-title">Suggested Next Steps:</div>
                      <ul className="next-steps-list">
                        {parsed.yourProfile.nextSteps.map((step: string, idx: number) => (
                          <li key={idx}>{step}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            {parsed.quickActions && parsed.quickActions.options && parsed.quickActions.options.length > 0 && (
              <div className="hierarchy-section">
                <div className="hierarchy-section-title">{parsed.quickActions.title}</div>
                <div className="quick-actions-grid">
                  {parsed.quickActions.options.map((action: string, index: number) => (
                    <button
                      key={index}
                      className="quick-action-btn hierarchy-action"
                      onClick={() => onSuggestionClick?.(action)}
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'unsupported_action':
        return (
          <div className="response-container">
            <div className="response-title error-title">
              {parsed.title}
            </div>
            <div className="response-message error-message">{parsed.message}</div>
            {parsed.suggestions && parsed.suggestions.length > 0 && (
              <SuggestionChips 
                suggestions={parsed.suggestions} 
                onSuggestionClick={onSuggestionClick!} 
              />
            )}
          </div>
        );

      case 'access_denied':
        return (
          <div className="response-container">
            <div className="response-title warning-title">
              {parsed.title}
            </div>
            <div className="response-message warning-message">{parsed.message}</div>
            {parsed.suggestions && parsed.suggestions.length > 0 && (
              <SuggestionChips 
                suggestions={parsed.suggestions} 
                onSuggestionClick={onSuggestionClick!} 
              />
            )}
          </div>
        );

      case 'security_violation':
        return (
          <div className="response-container">
            <div className="response-title security-title">
              {parsed.title}
            </div>
            <div className="response-message security-message">{parsed.message}</div>
            {parsed.suggestions && parsed.suggestions.length > 0 && (
              <SuggestionChips 
                suggestions={parsed.suggestions} 
                onSuggestionClick={onSuggestionClick!} 
              />
            )}
          </div>
        );

      case 'chat_message':
        return (
          <div className="response-container">
            {parsed.title && (
              <div className="response-title">{parsed.title}</div>
            )}
            <div
              className="response-message"
              dangerouslySetInnerHTML={{ __html: formatRichMarkdown(parsed.message || message.content) }}
            />
            <TokenUsageLine usage={parsed.usage} />
            {false && parsed.usage && (parsed.usage.input != null || parsed.usage.output != null) && (
              <div className="chat-token-usage">
                Input tokens: {parsed.usage.input ?? 0} · Output tokens: {parsed.usage.output ?? 0}
              </div>
            )}
          </div>
        );

      case 'text':
      default:
        return (
          <div className="response-container">
            <div
              className="response-message"
              dangerouslySetInnerHTML={{ __html: formatRichMarkdown(parsed.message || message.content) }}
            />
            <TokenUsageLine usage={parsed.usage} />
            {false && parsed.usage && (parsed.usage.input != null || parsed.usage.output != null) && (
              <div className="chat-token-usage">
                Input tokens: {parsed.usage.input ?? 0} · Output tokens: {parsed.usage.output ?? 0}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="message-bubble assistant">
      <div className="message-content">
        {renderContent()}
      </div>
      <div className="message-footer">
        <div className="message-timestamp">{formatTime(message.createdAt)}</div>
        <div className="message-actions">
          {onReaction && (
            <div className="message-reactions">
              <button
                className="reaction-btn"
                onClick={() => onReaction(message.id, 'up')}
                title="Helpful"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                </svg>
              </button>
              <button
                className="reaction-btn"
                onClick={() => onReaction(message.id, 'down')}
                title="Not helpful"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                </svg>
              </button>
            </div>
          )}
          {onCopy && (
            <button
              className="message-copy-btn"
              onClick={() => onCopy(message.content, message.id)}
              title={copiedId === message.id ? 'Copied!' : 'Copy message'}
            >
              {copiedId === message.id ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Welcome Screen Component
const WelcomeScreen: React.FC<{ onSuggestionClick: (text: string) => void }> = ({ onSuggestionClick }) => (
  <div className="welcome-screen">
    <h3 className="welcome-title">Hi! I'm Creyon, your AI assistant</h3>
    <p className="welcome-subtitle">I can help you with expenses, team management, company policies, and much more</p>
  </div>
);

// Main Chat Container Component
const ChatWidget: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [launcherRevealed, setLauncherRevealed] = useState(false);
  const [isFullscreen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const { messages, isLoading, error, sendMessage, clearError, sessionId } = useChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatHistoryStorageKey = user?.id ? `creon_chat_history_${user.id}` : 'creon_chat_history';
  const lastSessionStorageKey = user?.id ? `creon_last_session_${user.id}` : 'creon_last_session';

  useEffect(() => {
    localStorage.setItem('creyon_chat_widget_open', String(isOpen));
    localStorage.setItem('creyon_chat_widget_state', isOpen ? 'open' : 'expanded');
  }, [isOpen]);

  useEffect(() => {
    document.body.classList.toggle('chat-widget-open', isOpen);
    document.body.classList.toggle('chat-widget-collapsed', !isOpen);

    return () => {
      document.body.classList.remove('chat-widget-open', 'chat-widget-collapsed');
    };
  }, [isOpen]);

  // Handle fullscreen toggle - navigate to full page
  const handleFullscreenToggle = () => {
    // Check if we're already on the chat page
    if (window.location.pathname === '/chat') {
      return;
    }
    
    if (currentChatId) {
      localStorage.setItem(lastSessionStorageKey, currentChatId);
    }

    console.log('[ChatContinuity][WidgetBeforeNavigate]', {
      currentChatId,
      lastSessionStorageKey,
      lastSessionId: localStorage.getItem(lastSessionStorageKey),
      backendSessionId: sessionId || [...messages].reverse().find(message => message.sessionId)?.sessionId || null,
      messageCount: messages.length,
    });

    // Use React Router navigate for instant transition (no page reload)
    navigate('/chat');
    setIsOpen(false);
  };

  // Copy to clipboard handler
  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Load chat history from localStorage
  useEffect(() => {
    const loadChatHistory = () => {
      try {
        const legacyKey = 'creon_chat_history';
        const legacyStored = localStorage.getItem(legacyKey);
        if (user?.id && legacyStored && !localStorage.getItem(chatHistoryStorageKey)) {
          localStorage.setItem(chatHistoryStorageKey, legacyStored);
        }

        const stored = localStorage.getItem(chatHistoryStorageKey);
        if (stored) {
          const history = JSON.parse(stored);
          // Clean up chats older than 30 days
          const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
          const filtered = history.filter((chat: any) => 
            new Date(chat.lastUpdated).getTime() > thirtyDaysAgo
          );
          setChatHistory(filtered);
          // Save cleaned history back
          if (filtered.length !== history.length) {
            localStorage.setItem(chatHistoryStorageKey, JSON.stringify(filtered));
          }
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }
    };
    loadChatHistory();
  }, [chatHistoryStorageKey, user?.id]);

  // Save current chat to history
  useEffect(() => {
    if (messages.length > 0) {
      const chatTitle = messages[0]?.content.substring(0, 50) || 'New Chat';
      const chatId = currentChatId || `chat_${Date.now()}`;
      const backendSessionId = sessionId || [...messages].reverse().find(message => message.sessionId)?.sessionId || null;

      console.log('[ChatContinuity][WidgetSaveBefore]', {
        currentChatId,
        chatId,
        backendSessionId,
        messageCount: messages.length,
      });
      
      const chatData = {
        id: chatId,
        title: chatTitle,
        messages: messages,
        backendSessionId,
        lastUpdated: new Date().toISOString(),
        messageCount: messages.length
      };

      setChatHistory((previousHistory) => {
        const updatedHistory = [...previousHistory];
        const existingIndex = updatedHistory.findIndex(c => c.id === chatId);

        if (existingIndex >= 0) {
          updatedHistory[existingIndex] = chatData;
        } else {
          updatedHistory.unshift(chatData);
        }

        try {
          localStorage.setItem(chatHistoryStorageKey, JSON.stringify(updatedHistory));
          localStorage.setItem(lastSessionStorageKey, chatId);
          console.log('[ChatContinuity][WidgetSaveAfter]', {
            savedChatId: chatId,
            backendSessionId,
            lastSessionStorageKey,
            lastSessionId: localStorage.getItem(lastSessionStorageKey),
            messageCount: messages.length,
          });
        } catch (error) {
          console.error('Failed to save chat history:', error);
        }

        return updatedHistory;
      });
      setCurrentChatId(chatId);
    }
  }, [messages, currentChatId, chatHistoryStorageKey, lastSessionStorageKey, sessionId]);

  // Scroll to top handler
  const scrollToTop = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle scroll to show/hide scroll-to-top button
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      setShowScrollTop(scrollTop > 300 && scrollHeight > clientHeight + 300);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Esc to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
      // Ctrl+K or Cmd+K to toggle
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Handle message reactions
  const handleReaction = (messageId: string, reaction: 'up' | 'down') => {
    console.log(`Message ${messageId} received ${reaction} reaction`);
    // TODO: Send feedback to backend for analytics
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, isOpen, isLoading]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      const maxTextareaHeight = 150;
      inputRef.current.style.height = 'auto';
      const nextHeight = Math.min(inputRef.current.scrollHeight, maxTextareaHeight);
      inputRef.current.style.height = `${nextHeight}px`;
      inputRef.current.style.overflowY = inputRef.current.scrollHeight > maxTextareaHeight ? 'auto' : 'hidden';
    }
  }, [inputValue]);

  const handleOpen = () => {
    setLauncherRevealed(true);
    setIsOpen(true);
    if (error) clearError();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleRevealLauncher = () => {
    setLauncherRevealed(true);
    if (error) clearError();
  };

  const handleHideLauncher = () => {
    setIsOpen(false);
    setLauncherRevealed(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue.trim();
    setInputValue('');
    
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  const showWelcome = messages.length === 0 && !isLoading;

  return (
    <>
      {/* Chatbot FAB icon — bottom right */}
      <div className={`chat-launcher-group ${launcherRevealed ? 'revealed' : 'collapsed'}`}>
        {launcherRevealed ? (
          <div className="chat-launcher-shell">
            <button
              className="chat-launcher-collapse"
              onClick={handleHideLauncher}
              aria-label="Collapse chat assistant"
              title="Collapse chat assistant"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <button
              className={`chat-fab chat-launcher-icon ${isOpen ? 'open' : 'closed'}`}
              onClick={handleOpen}
              aria-label="Open chat assistant"
              aria-expanded={isOpen}
              aria-controls="creyon-chat-widget-panel"
              title="Creyon Assistant"
            >
              <ChatbotAvatar size="large" showChatBubble={true} />
            </button>
          </div>
        ) : (
          <button
            className="chat-edge-tab"
            onClick={handleRevealLauncher}
            aria-label="Show chat assistant"
            title="Show chat assistant"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </div>

      {/* Chat Window - Popup Widget (No Sidebar) */}
      {!isFullscreen && (
        <div
          id="creyon-chat-widget-panel"
          className={`chat-container ${isOpen ? 'open' : 'collapsed'}`}
          aria-hidden={!isOpen}
          role="dialog"
          aria-label="Creyon chat assistant"
        >
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <line x1="9" y1="9" x2="9.01" y2="9" />
                  <line x1="15" y1="9" x2="15.01" y2="9" />
                </svg>
              </div>
              <div className="chat-header-text">
                <h3>Creyon</h3>
                <span className="chat-status">
                  <span className="status-dot"></span>
                  {isLoading ? 'Typing...' : 'Online'}
                </span>
              </div>
            </div>
            <div className="chat-header-actions">
              <button 
                className="chat-action-btn" 
                onClick={handleFullscreenToggle} 
                aria-label="Open in full page"
                title="Open in full page"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              </button>
              <button className="chat-close-btn" onClick={handleClose} aria-label="Close chat assistant">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages Container */}
          <div className="chat-messages" ref={messagesContainerRef} onScroll={handleScroll}>
            {showWelcome ? (
              <WelcomeScreen onSuggestionClick={handleSuggestionClick} />
            ) : (
              <>
                {messages.map((message) => (
                  <MessageBubble 
                    key={message.id} 
                    message={message} 
                    onCopy={handleCopy}
                    copiedId={copiedId}
                    onReaction={handleReaction}
                    onSuggestionClick={handleSuggestionClick}
                  />
                ))}
                
                {isLoading && (
                  <div className="message-bubble assistant">
                    <div className="message-content">
                      <SkeletonLoader count={2} />
                    </div>
                  </div>
                )}
                
                {error && (
                  <div className="chat-error-message">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
            
            {/* Scroll to Top Button */}
            {showScrollTop && (
              <button className="scroll-to-top-btn" onClick={scrollToTop} title="Scroll to top">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </button>
            )}
          </div>

          {/* Input Box */}
          <div className="chat-input-container">
            <form className="chat-input-form" onSubmit={handleSubmit}>
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything..."
                disabled={isLoading}
                className="chat-textarea"
                rows={1}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className="chat-send-btn"
                aria-label="Send message"
              >
                {isLoading ? (
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
